import { useState } from 'react';
import { useDecayTimer, type SpeedMultiplier } from '@/hooks/useDecayTimer';
import { DEFAULT_DECAY_CURVE, getTierColor, getTierLabel } from '@/lib/decay';
import { cn } from '@/lib/utils';
import { SectionHeading } from '@/components/ui/SectionHeading';
import { DealCard } from '@/components/ui/DealCard';
import { DecayBar } from '@/components/ui/DecayBar';
import { TierIndicator } from '@/components/ui/TierIndicator';
import { Button } from '@/components/ui/Button';

const SPEEDS: SpeedMultiplier[] = [1, 10, 60];

export function DecayDemo() {
  const [speed, setSpeed] = useState<SpeedMultiplier>(1);
  const timer = useDecayTimer(DEFAULT_DECAY_CURVE, speed);

  function handleSpeed(s: SpeedMultiplier) {
    setSpeed(s);
    timer.controls.setSpeed(s);
  }

  return (
    <section
      id="decay-demo"
      className="bg-gradient-to-b from-slate-900 to-slate-800 py-24"
    >
      <div className="container mx-auto px-6">
        <SectionHeading
          eyebrow="See It In Action"
          title="Watch the discount decay in real-time"
          className="[&_h2]:text-white [&_span]:text-brand-400"
        />

        <div className="mx-auto mt-12 max-w-md">
          {/* Glass card */}
          <div className="glass rounded-2xl p-6">
            <DealCard
              dealName="Premium Running Shoes"
              originalPrice={129.99}
              currentDiscount={timer.discountPercent}
              tierLabel={getTierLabel(timer.currentTierIndex)}
              tierColor={getTierColor(timer.currentTierIndex)}
              timeRemaining={timer.secondsUntilNextDrop ?? 0}
            />

            <div className="mt-6">
              <DecayBar
                curve={DEFAULT_DECAY_CURVE}
                progress={timer.progress}
                currentTierIndex={timer.currentTierIndex}
              />
            </div>

            <div className="mt-4">
              <TierIndicator
                tierIndex={timer.currentTierIndex}
                discountPercent={timer.discountPercent}
                secondsUntilDrop={timer.secondsUntilNextDrop}
              />
            </div>

            {/* Speed controls */}
            <div className="mt-6 flex items-center gap-3">
              {SPEEDS.map((s) => (
                <button
                  key={s}
                  onClick={() => handleSpeed(s)}
                  className={cn(
                    'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                    speed === s
                      ? 'bg-brand-500 text-white'
                      : 'bg-white/10 text-gray-300 hover:bg-white/20',
                  )}
                >
                  {s}x
                </button>
              ))}
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto text-gray-300 hover:text-white"
                onClick={() => timer.controls.reset()}
              >
                Reset
              </Button>
            </div>
          </div>

          <p className="mt-6 text-center text-sm text-gray-400">
            Discounts start high and decay through tiers as time passes. Early
            arrivals lock in the best price.
          </p>
        </div>
      </div>
    </section>
  );
}
